import { faPhoneVolume as farPhoneVolume } from "@awesome.me/kit-3777c2145e/icons/classic/regular";
import type { AbstractLeadFormState } from "@component/assets/AbstractLeadForm/AbstractLeadForm";
import type { CalculatorError } from "@component/assets/CalculatorError";
import { Config } from "@config";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { formatText, formatTextWhenNotZero } from "@ui/format";
import { GoogleAnalytics4 } from "@utils/google-analytics-4";
import { logError } from "@utils/sentry-error";
import autobind from "autobind-decorator";
import React from "react";
import type { ReactNode } from "react";
import { DEFAULT_PARAMS } from "../Calculator/data";
import { calculate, responseToResult, searchParamsToRequest } from "../Calculator/domain";
import type { MortgageResponseItem, MortgageSearchParams } from "../Calculator/types";
import { MortgageLeadForm } from "../LeadForm/MortgageLeadForm";
import type { MortgageLeadFormFields, MortgageLeadFormProps } from "../LeadForm/MortgageLeadForm";
import type { MortgageLeadCalcRequest, MortgageLeadCalcResponse } from "../LeadForm/types";
import * as Step from "./Steps";
import { INTEREST_PERIODS } from "./data";
import "./style.scss";

interface FormFields extends MortgageLeadFormFields {
	loanAmount: number;
	maturity: number;
	income: number;
	realEstateValue: number;
	inKhr: boolean;
	numberOfChildrenCurrent: number;
	numberOfChildrenCommitment: number;
	eligibleForCsokPlus: boolean;
	qualifiesForHigherLtv: boolean;
	qualifiesForVoluntaryInterestRateCap: boolean;
}

interface State extends AbstractLeadFormState<FormFields> {
	search: Record<string, any>;
	errorMessages: string[];
	mortgageResponses: MortgageResponseItem[];
	selected: MortgageResponseItem;
}

/**
 * @Shortcode
 *
 * @title "Lakáshitel kalkulátor"
 * @shortcode "mortgages_small"
 * @slug "lakashitel"
 * @type "calculator"
 */
export class MortgageSmallCalculator extends MortgageLeadForm<
	FormFields,
	MortgageSearchParams,
	MortgageLeadFormProps<MortgageSearchParams>,
	State
> {
	// EventParams
	protected readonly productTypes: string = "MORTGAGE";
	protected readonly componentType: string = "small-calculator";

	public static defaultProps = {
		title: "",
		outsideTitle: "Lakáshitel kalkulátor",
		outsideTitleLink: "/lakashitel-kalkulator/",
		bottomLogo: true,
		colorScheme: "dark",
		withLoanAmount: "auto",
	};
	private isPartner: boolean;

	public constructor(props: MortgageLeadFormProps<MortgageSearchParams>) {
		super(props);
		this.isPartner = Config.isPartner();
	}

	protected getInitialState(): State {
		return {
			...super.getInitialState(),
			activeStep: `step-1`,
			search: undefined,
			errorMessages: undefined,
			mortgageResponses: [],
			selected: undefined,
		};
	}

	protected renderSteps(): ReactNode {
		const {
			submitState,
			values: { loanAmount, maturity, email },
			mortgageResponses,
			selected,
			errorMessages,
			leadId,
		} = this.state;

		const { variant } = this.props;

		return (
			<>
				<Step.Step1 stepName="step-1" defaultTarget="step-2" submitState={submitState} variant={variant} />
				<Step.Step2
					stepName="step-2"
					defaultTarget="result"
					submitState={submitState}
					variant={variant}
					canStepBack
				/>
				<Step.Khr stepName="khr" variant={variant} />
				{this.isPartner ? (
					<Step.PartnerResult
						stepName="result"
						defaultTarget="lead-form"
						canStepBack
						loanAmount={loanAmount}
						maturity={maturity}
						results={mortgageResponses}
						onSelect={this.onSelectProduct}
					/>
				) : (
					<Step.Result
						stepName="result"
						defaultTarget="lead-form"
						canStepBack
						loanAmount={loanAmount}
						maturity={maturity}
						result={mortgageResponses.length > 0 ? mortgageResponses[0] : undefined}
						variant={variant}
					/>
				)}
				<Step.JtmKoPage stepName="jtm-ko-page" canStepBack />
				<Step.ResultError stepName="result-error" canStepBack errorList={errorMessages} />
				{this.isPartner ? (
					<Step.PartnerLeadForm
						stepName="lead-form"
						defaultTarget="success-lead"
						canStepBack
						submitState={submitState}
						withLoanAmount={false}
						loanAmount={loanAmount}
						maturityYears={maturity}
						installment={selected?.installmentStart}
						apr={selected?.apr}
						variant={undefined}
					/>
				) : (
					<Step.LeadForm
						stepName="lead-form"
						defaultTarget="success-lead"
						canStepBack
						submitState={submitState}
						withLoanAmount={false}
						title={this.renderLeadFormTitle()}
						button="Eredmény küldése"
						variant={undefined}
						crossSellOption
					/>
				)}
				<Step.SuccessPage
					stepName="success-lead"
					leadId={leadId}
					userEmail={email}
					variant={variant}
					ga4SendEventParameters={{
						providerName: selected?.bank?.name,
						productName: selected?.name,
						productId: selected?.id,
					}}
				/>
			</>
		);
	}

	@autobind
	private onSelectProduct(index: number, result: MortgageResponseItem): void {
		this.setState({
			selected: result,
		});

		this.sendEvent("BM_resultItemButton", {
			buttonId: GoogleAnalytics4.generateId("bi"),
			position: index + 1,
			buttonName: `detail`,
			providerName: result.bank,
			productName: result.name,
			productId: result.id,
		});
	}

	private renderLeadFormTitle(): ReactNode {
		return (
			<div className="d-flex py-2 align-items-center">
				<div className="pr-2">
					<FontAwesomeIcon icon={farPhoneVolume} className="text-gold" size="3x" />
				</div>
				<div className="small border-left pl-2">
					Kérd részletes eredményeidet email-ben és várd szakértőnk hívását, hogy segítsen a felmerülő
					kérdések tisztázásában!
				</div>
			</div>
		);
	}

	protected onStep(target: string, withEvent: boolean): void {
		const {
			values: { inKhr },
		} = this.state;
		switch (target) {
			case "result":
				if (inKhr) {
					super.onStep("khr", withEvent);
				} else {
					this.onResult(target);
				}
				break;
			default:
				super.onStep(target, withEvent);
		}
	}

	@autobind
	private onResult(target): void {
		const {
			values: {
				loanAmount,
				maturity,
				income,
				realEstateValue,
				numberOfChildrenCurrent,
				numberOfChildrenCommitment,
				eligibleForCsokPlus,
				qualifiesForHigherLtv,
				qualifiesForVoluntaryInterestRateCap,
			},
		} = this.state;
		const { variant } = this.props;

		this.setState({ submitState: "loading" });
		const searchParams = {
			...DEFAULT_PARAMS,
			loanGoal: variant === "freeUse" ? "SZAB_FEL" : DEFAULT_PARAMS.loanGoal,
			interestPeriods: INTEREST_PERIODS,
			income,
			loanAmount,
			maturity,
			onePerBank: false,
			onlyGreen: false,
			realEstateValue,
			numberOfChildrenCurrent,
			numberOfChildrenCommitment,
			eligibleForCsokPlus,
			qualifiesForHigherLtv,
			qualifiesForVoluntaryInterestRateCap,
		};

		const errors = [];

		const request = searchParamsToRequest(searchParams, "THM");
		this.promisePool
			.add(calculate(request))
			.then((response) => {
				const results = responseToResult(request, response, "without-ct");

				if (results?.normalList?.length) {
					this.setState({
						submitState: undefined,
						mortgageResponses: results.normalList,
						selected: results.normalList?.length > 0 && !this.isPartner ? results.normalList[0] : undefined,
						search: request,
					});
				} else if (!errors.length) {
					errors.push("Nincs számodra megfelelő a bankok kínálatai között.");
				}
			})
			.catch((error: CalculatorError) => {
				if (error?.fieldErrors) {
					Object.values(error?.fieldErrors).forEach((errorMsg: string) => {
						errors.push(errorMsg);
					});
				} else if (error?.message) {
					errors.push(`${error?.message.split(".")[0]}.`);
					errors.push("JTM");
				} else {
					errors.push("Ismeretlen hiba történt a kalkuláció közben!");
					logError(error);
				}
			})
			.finally(() => {
				this.setState(
					{
						submitState: undefined,
						errorMessages: errors,
					},
					() => {
						this.sendCalculationEvent(errors);

						if (errors.some((error) => error.includes("JTM"))) {
							super.onStep("jtm-ko-page", true);
						} else {
							super.onStep(errors.length ? "result-error" : target, true);
						}
					},
				);
			});
	}

	protected getCalculatorRequest(): MortgageLeadCalcRequest {
		const {
			values: { income, maturity, realEstateValue },
		} = this.state;
		const request = { ...super.getCalculatorRequest() };
		request.incomeList = [
			{
				incomeType: "SALARY-H",
				incomeTypeName: "Magyar alkalmazotti jövedelem",
				amount: income,
				receiptAmount: income,
			},
		];

		request.maturity = maturity;
		request.realEstateValue = realEstateValue;

		return request;
	}

	protected getCalculatorResponse(): MortgageLeadCalcResponse {
		const {
			selected: {
				subType,
				apr,
				installmentStart,
				allRecovered,
				interestPeriodAided,
				interestPeriodNotAided,
				bank,
				name,
			},
		} = this.state;
		let productName;

		if (this.isPartner) {
			productName = subType ? `${name} - ${subType}` : name;
		}

		return {
			productName,
			bankCode: bank?.code,
			bankName: bank?.name,
			apr,
			installmentStart,
			allRecovered,
			interestPeriod: interestPeriodAided?.month || interestPeriodNotAided?.month,
		};
	}

	protected getClassName(): string {
		return `${super.getClassName()} mortgage react-small-calculator`;
	}

	protected getAutoresponderName(): string {
		return "autoresponder-mortgages-article";
	}

	protected getAutoresponderFields(): Record<string, any> {
		const {
			values: { lastName, firstName, loanAmount, maturity, income },
			selected: mortgageResponse,
			search: { qualifiesForVoluntaryInterestRateCap },
		} = this.state;
		const { variant } = this.props;
		const expenses = mortgageResponse?.expenseGroups
			? mortgageResponse.expenseGroups?.MODIFICATION_FEES?.expenses
			: undefined;
		let interestRate: number;
		let interestPeriodName = "";

		if (mortgageResponse?.loanType === "marketLoan") {
			interestPeriodName = mortgageResponse?.interestPeriodNotAided.name;
			interestRate = mortgageResponse?.interestNotAided;
		} else {
			interestPeriodName = `Támogatás alatt:<br />${
				mortgageResponse?.interestPeriodAided?.name || " - "
			}<br />Támogatás után:<br />${mortgageResponse?.interestPeriodNotAided?.name || " - "}`;
			interestRate = mortgageResponse?.interestAided;
		}

		return {
			lastName,
			firstName,
			productName: mortgageResponse.name,
			loanAmount: formatText(loanAmount, "ft"),
			loanGoal: variant === "freeUse" ? "SZAB_FEL" : DEFAULT_PARAMS.loanGoal,
			maturityYears: maturity,
			sumIncome: formatText(income, "ft"),
			installmentStart: formatText(mortgageResponse?.installmentStart, "ft"),
			interestRate: formatText(interestRate, "%"),
			interestPeriodName,
			allRecovered: formatText(mortgageResponse?.allRecovered, "ft"),
			ETKALTexpenseString: expenses?.ETKALT?.expenseString,
			apr: formatText(mortgageResponse?.apr, "%"),
			ETKONexpenseString: expenses?.ETKON?.expenseString,
			ETKMHexpenseString: expenses?.ETKMH?.expenseString,
			VTKALTexpenseString: expenses?.VTKALT?.expenseString,
			VTKONexpenseString: expenses?.VTKON?.expenseString,
			VTKMHexpenseString: expenses?.VTKMH?.expenseString,
			startUpCost: formatText(mortgageResponse?.startUpCost, "ft"),
			startUpCostRefund: formatTextWhenNotZero(mortgageResponse?.startUpCostRefund, "ft"),
			interestDiscount: formatTextWhenNotZero(mortgageResponse?.bmDiscounts?.interestDiscount, "ft"),
			earlyRepaymentDiscountInGivenMonth: formatTextWhenNotZero(
				mortgageResponse?.bmDiscounts?.earlyRepaymentDiscountInGivenMonth,
				"ft",
			),
			totalSavings: formatTextWhenNotZero(mortgageResponse?.bmDiscounts?.totalSavings, "ft"),
			...(qualifiesForVoluntaryInterestRateCap && {
				discountedPeriodInstallment: formatText(mortgageResponse?.discountedPeriodInstallment, "ft"),
				discountedInterest: formatText(mortgageResponse?.discountedInterest, "%"),
				discountedPeriod: formatText(mortgageResponse?.discountedPeriod, "év"),
			}),
		};
	}

	protected getComponentName(): string {
		return `${super.getComponentName()}.MortgageSmallCalculator`;
	}
}

export default MortgageSmallCalculator;
